import { useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
    Button,
    Dialog,
    DialogContent,
    DialogTitle,
    Divider,
    MenuItem,
    Tooltip,
    Typography
} from '@mui/material';
import { selectAccess } from '../../../store/userReducer';
import { useClinic } from '../../../utils/ClinicContext';
import { useRole } from '../../../utils/RoleContext';
import { getMedicalRoles, isMedicalRole } from '../../../utils/accessSelection';

const ClinicAccessSelector = () => {
    const { t } = useTranslation();
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const { role, setRole } = useRole();
    const { setClinic } = useClinic();
    const user = useSelector((state) => state.user);
    const roles = getMedicalRoles(user.roles).filter((item) => item.account?.uuid);
    const [open, setOpen] = useState(false);
    const [selectedClinicUuid, setSelectedClinicUuid] = useState(null);
    const clinicName = user.selectedClinic?.name || t('profile.labels.clinic');
    const roleLabels = {
        PATIENT: t('profile.roles.PATIENT'),
        DOCTOR: t('profile.roles.DOCTOR'),
        PATIENT_CARE_GIVER: t('profile.roles.PATIENT_CARE_GIVER'),
        CLINIC_CARE_GIVER: t('profile.roles.CLINIC_CARE_GIVER')
    };
    const activeRoleName =
        (user.roles || []).find((item) => item.uuid === user.selectedRole)?.name || role;
    if (!isMedicalRole({ name: activeRoleName })) return null;

    const handleSelectRole = (roleUuid) => {
        const selectedRole = roles.find(
            (item) => item.uuid === roleUuid && item.account.uuid === selectedClinicUuid
        );
        if (!selectedRole) return;
        const clinic = selectedRole.account;
        dispatch(selectAccess({ clinic, roleUuid }));
        setClinic(clinic);
        sessionStorage.setItem('clinic', JSON.stringify(clinic));
        setRole(selectedRole.name);
        sessionStorage.setItem('role', selectedRole.name);
        setOpen(false);
        navigate('/');
    };

    return (
        <>
            <Tooltip title={`${clinicName} - ${t('profile.changeClinicAndRole')}`}>
                <Button
                    color="primary"
                    disabled={roles.length === 0}
                    aria-label={`${t('profile.changeClinicAndRole')}: ${clinicName}`}
                    aria-haspopup="dialog"
                    aria-expanded={open}
                    onClick={() => {
                        setSelectedClinicUuid(user.selectedClinic?.uuid || null);
                        setOpen(true);
                    }}
                    sx={{
                        mt: 2,
                        mr: { xs: 0.5, sm: 1.5 },
                        minWidth: 0,
                        maxWidth: { xs: 150, sm: 240, lg: 320 },
                        flexShrink: 0,
                        height: 40,
                        px: 1,
                        color: '#0A579B',
                        fontWeight: 600,
                        textTransform: 'none'
                    }}
                >
                    <Typography
                        component="span"
                        noWrap
                        sx={{ minWidth: 0, fontSize: '0.875rem', fontWeight: 'inherit' }}
                    >
                        {clinicName}
                    </Typography>
                </Button>
            </Tooltip>
            <Dialog
                open={open}
                onClose={() => setOpen(false)}
                fullWidth
                aria-labelledby="clinic-access-dialog-title"
            >
                <DialogTitle
                    id="clinic-access-dialog-title"
                    sx={{ fontSize: '1.3rem', fontWeight: 'bold', textAlign: 'center' }}
                >
                    {t('profile.dialog.selectClinicAndRole')}
                </DialogTitle>
                <DialogContent>
                    <Typography
                        variant="subtitle1"
                        sx={{
                            mb: 1,
                            fontWeight: 'bold',
                            color: '#1976d2',
                            fontSize: '1.2rem'
                        }}
                    >
                        {t('profile.labels.clinic')}
                    </Typography>
                    {Array.from(
                        new Map(
                            roles.map((item) => [item.account.uuid, item.account])
                        ).values()
                    ).map(({ uuid, name }) => (
                        <MenuItem
                            key={uuid}
                            onClick={() => setSelectedClinicUuid(uuid)}
                            selected={uuid === selectedClinicUuid}
                            sx={{
                                mb: 0.5,
                                borderRadius: 1,
                                bgcolor:
                                    uuid === selectedClinicUuid ? '#1976d2' : 'inherit',
                                color: uuid === selectedClinicUuid ? 'white' : 'inherit',
                                '&.Mui-selected': {
                                    bgcolor: '#1976d2 !important',
                                    color: 'white !important',
                                    '&:hover': { bgcolor: '#115293 !important' }
                                }
                            }}
                        >
                            {name}
                        </MenuItem>
                    ))}
                    <Divider sx={{ my: 2 }} />
                    <Typography
                        variant="subtitle1"
                        sx={{
                            mb: 1,
                            fontWeight: 'bold',
                            color: '#1976d2',
                            fontSize: '1.2rem'
                        }}
                    >
                        {t('profile.labels.profile')}
                    </Typography>
                    {roles
                        .filter((item) => item.account.uuid === selectedClinicUuid)
                        .map(({ uuid, name }) => (
                            <MenuItem
                                key={uuid}
                                onClick={() => handleSelectRole(uuid)}
                                sx={{ mb: 0.5, borderRadius: 1 }}
                            >
                                {roleLabels[name] || name}
                            </MenuItem>
                        ))}
                    {(!selectedClinicUuid ||
                        !roles.some(
                            (item) => item.account.uuid === selectedClinicUuid
                        )) && (
                        <Typography variant="caption" color="textSecondary">
                            {t('profile.helper.selectClinicFirst')}
                        </Typography>
                    )}
                </DialogContent>
            </Dialog>
        </>
    );
};

export default ClinicAccessSelector;
